import { useEffect, useRef } from "react";

const LEAVE_DELAY_MS = 300;

/** Calls `onOutsideAction` on a mouse press outside the element, or once the pointer has been away from it a moment. */
export const useCloseOnOutsideAction = <Element extends HTMLElement>({
  enabled,
  onOutsideAction,
}: {
  enabled: boolean;
  onOutsideAction: () => void;
}) => {
  const ref = useRef<Element>(null);

  useEffect(
    function closeOnOutsideAction() {
      const element = ref.current;
      if (!enabled || !element) return;

      let leaveTimer: ReturnType<typeof setTimeout> | undefined;

      const handleMouseDown = (event: MouseEvent) => {
        if (!element.contains(event.target as Node)) onOutsideAction();
      };

      const handleMouseLeave = () => {
        leaveTimer = setTimeout(onOutsideAction, LEAVE_DELAY_MS);
      };

      const handleMouseEnter = () => {
        clearTimeout(leaveTimer);
      };

      window.addEventListener("mousedown", handleMouseDown);
      element.addEventListener("mouseleave", handleMouseLeave);
      element.addEventListener("mouseenter", handleMouseEnter);

      return () => {
        clearTimeout(leaveTimer);
        window.removeEventListener("mousedown", handleMouseDown);
        element.removeEventListener("mouseleave", handleMouseLeave);
        element.removeEventListener("mouseenter", handleMouseEnter);
      };
    },
    [enabled, onOutsideAction],
  );

  return ref;
};
